import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Loader2, CreditCard, UserPlus, ArrowLeft, Users, CalendarClock, Search,
  Plus, Pencil, Trash2, X, Check,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import {
  getAllMockTestSubscriptionPlans,
  createMockTestSubscriptionPlan,
  updateMockTestSubscriptionPlan,
  deleteMockTestSubscriptionPlan,
  getAllMockTestSubscriptions,
  adminGrantMockTestSubscription,
  type MockTestSubscription,
  type MockTestSubscriptionPlan,
} from "@/api/lms/mock-tests";

type PlanForm = {
  title: string;
  durationMonths: number;
  price: number;
  isActive: boolean;
};

const emptyForm: PlanForm = { title: "", durationMonths: 1, price: 0, isActive: true };

export default function AdminMockTestSubscriptionsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [grantUserId, setGrantUserId] = useState("");
  const [grantPlanId, setGrantPlanId] = useState("");
  const [grantRef, setGrantRef] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Plan create/edit form state.
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [newPlan, setNewPlan] = useState<PlanForm>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PlanForm>(emptyForm);

  const { data: plans = [] } = useQuery({
    queryKey: ["allMockTestSubscriptionPlans"],
    queryFn: getAllMockTestSubscriptionPlans,
  });

  const { data: subsData, isLoading } = useQuery({
    queryKey: ["allMockTestSubscriptions"],
    queryFn: () => getAllMockTestSubscriptions({ page: 1, limit: 100 }),
  });

  const invalidatePlans = () => {
    queryClient.invalidateQueries({ queryKey: ["allMockTestSubscriptionPlans"] });
    queryClient.invalidateQueries({ queryKey: ["mockTestSubscriptionPlans"] });
  };

  const createPlanMutation = useMutation({
    mutationFn: () => createMockTestSubscriptionPlan(newPlan),
    onSuccess: () => {
      toast.success("Plan created");
      invalidatePlans();
      setShowNewPlan(false);
      setNewPlan(emptyForm);
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to create plan"),
  });

  const updatePlanMutation = useMutation({
    mutationFn: (id: string) => updateMockTestSubscriptionPlan(id, editForm),
    onSuccess: () => {
      toast.success("Plan updated");
      invalidatePlans();
      setEditingId(null);
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to update plan"),
  });

  const deletePlanMutation = useMutation({
    mutationFn: (id: string) => deleteMockTestSubscriptionPlan(id),
    onSuccess: () => {
      toast.success("Plan deleted");
      invalidatePlans();
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to delete plan"),
  });

  const grantMutation = useMutation({
    mutationFn: () =>
      adminGrantMockTestSubscription({
        userId: Number(grantUserId),
        planId: grantPlanId,
        paymentRef: grantRef.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success("Subscription granted!");
      queryClient.invalidateQueries({ queryKey: ["allMockTestSubscriptions"] });
      setGrantUserId("");
      setGrantRef("");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to grant subscription"),
  });

  if (!user || user.role !== "admin") return null;

  const subs = subsData?.items ?? [];
  const filtered = subs.filter((s: MockTestSubscription) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const email = (s.user as any)?.email?.toLowerCase() ?? "";
    const name = (s.user as any)?.name?.toLowerCase() ?? "";
    return email.includes(q) || name.includes(q) || String(s.userId).includes(q);
  });

  const startEdit = (plan: MockTestSubscriptionPlan) => {
    setEditingId(plan.id);
    setEditForm({
      title: plan.title,
      durationMonths: plan.durationMonths,
      price: Number(plan.price),
      isActive: plan.isActive,
    });
  };

  return (
    <DashboardLayout>
      <div className="dash-page">
        {/* Header */}
        <div className="dash-page-header mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2 h-8 shrink-0" asChild>
              <Link to="/mock-tests"><ArrowLeft className="size-4 mr-1" />Mock Tests</Link>
            </Button>
            <div className="min-w-0">
              <h1 className="dash-page-title flex items-center gap-2 truncate">
                <CreditCard className="text-primary size-6 shrink-0" />
                All-Access Subscriptions
              </h1>
              <p className="dash-page-subtitle truncate">
                Manage subscription plans and student subscriptions
              </p>
            </div>
          </div>
          <Button size="sm" asChild variant="outline" className="rounded-xl shrink-0">
            <Link to="/admin/mock-test-purchases">Purchase Management</Link>
          </Button>
        </div>

        {/* Plans management */}
        <KCard className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Subscription Plans
            </Label>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs rounded-xl"
              onClick={() => { setShowNewPlan((v) => !v); setNewPlan(emptyForm); }}
            >
              <Plus className="size-3.5 mr-1" /> New Plan
            </Button>
          </div>

          {/* New plan form */}
          {showNewPlan && (
            <div className="rounded-xl border border-dashed border-primary/40 bg-muted/20 p-3 grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-[10px] font-semibold">Title</Label>
                <Input className="h-8 text-sm rounded-lg" placeholder="e.g. 3 Months All-Access"
                  value={newPlan.title} onChange={(e) => setNewPlan({ ...newPlan, title: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-semibold">Months</Label>
                <Input type="number" min={1} className="h-8 text-sm rounded-lg"
                  value={newPlan.durationMonths} onChange={(e) => setNewPlan({ ...newPlan, durationMonths: Math.max(1, Number(e.target.value) || 1) })} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-semibold">Price (BDT)</Label>
                <Input type="number" min={0} className="h-8 text-sm rounded-lg"
                  value={newPlan.price} onChange={(e) => setNewPlan({ ...newPlan, price: Math.max(0, Number(e.target.value) || 0) })} />
              </div>
              <Button className="h-8 text-xs rounded-lg font-bold" disabled={!newPlan.title.trim() || createPlanMutation.isPending}
                onClick={() => createPlanMutation.mutate()}>
                {createPlanMutation.isPending ? <Loader2 className="animate-spin size-3.5" /> : "Create"}
              </Button>
            </div>
          )}

          {/* Plans list */}
          <div className="space-y-2">
            {plans.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No plans yet. Create one above.</p>
            ) : plans.map((plan) => (
              editingId === plan.id ? (
                <div key={plan.id} className="rounded-xl border border-primary/40 bg-muted/20 p-3 grid grid-cols-1 sm:grid-cols-6 gap-2 items-end">
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-[10px] font-semibold">Title</Label>
                    <Input className="h-8 text-sm rounded-lg" value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold">Months</Label>
                    <Input type="number" min={1} className="h-8 text-sm rounded-lg" value={editForm.durationMonths}
                      onChange={(e) => setEditForm({ ...editForm, durationMonths: Math.max(1, Number(e.target.value) || 1) })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-semibold">Price</Label>
                    <Input type="number" min={0} className="h-8 text-sm rounded-lg" value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: Math.max(0, Number(e.target.value) || 0) })} />
                  </div>
                  <label className="flex items-center gap-1.5 text-[11px] font-medium pb-1.5">
                    <input type="checkbox" checked={editForm.isActive}
                      onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })} />
                    Active
                  </label>
                  <div className="flex gap-1">
                    <Button size="sm" className="h-8 flex-1 text-xs rounded-lg" disabled={updatePlanMutation.isPending}
                      onClick={() => updatePlanMutation.mutate(plan.id)}>
                      {updatePlanMutation.isPending ? <Loader2 className="animate-spin size-3.5" /> : <Check className="size-3.5" />}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-lg" onClick={() => setEditingId(null)}>
                      <X className="size-3.5" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div key={plan.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border border-border/60 bg-muted/20">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`px-2 py-0.5 rounded text-[10px] font-bold ${plan.isActive ? "bg-emerald-100 text-emerald-700" : "bg-stone-200 text-stone-500"}`}>
                      {plan.isActive ? "Active" : "Off"}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{plan.title}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {plan.durationMonths} month{plan.durationMonths !== 1 ? "s" : ""} · {plan.currency} {Number(plan.price).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-lg" onClick={() => startEdit(plan)}>
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-lg hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
                      disabled={deletePlanMutation.isPending}
                      onClick={() => { if (window.confirm(`Delete plan "${plan.title}"?`)) deletePlanMutation.mutate(plan.id); }}>
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              )
            ))}
          </div>
        </KCard>

        {/* Grant subscription */}
        <KCard className="p-5 space-y-4">
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Grant Subscription to Student
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Student User ID</Label>
              <Input type="number" placeholder="e.g. 42" className="h-8 text-sm rounded-xl"
                value={grantUserId} onChange={(e) => setGrantUserId(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Plan</Label>
              <select
                className="w-full h-8 px-3 rounded-xl border border-border bg-background text-sm outline-none focus:ring-2 focus:ring-primary"
                value={grantPlanId}
                onChange={(e) => setGrantPlanId(e.target.value)}
              >
                <option value="">— Choose a plan —</option>
                {plans.filter((p) => p.isActive).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} — {p.currency} {p.price}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Payment Ref (optional)</Label>
              <Input placeholder="e.g. TXN123" className="h-8 text-sm rounded-xl"
                value={grantRef} onChange={(e) => setGrantRef(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button className="w-full h-8 text-xs rounded-xl font-bold"
                disabled={!grantUserId || !grantPlanId || grantMutation.isPending}
                onClick={() => grantMutation.mutate()}>
                {grantMutation.isPending ? <Loader2 className="animate-spin size-3.5 mr-1" /> : <UserPlus className="size-3.5 mr-1" />}
                Grant
              </Button>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Renewals stack: if the student already has an active subscription, the new duration is added on top of the current expiry.
          </p>
        </KCard>

        {/* Subscriptions list */}
        <KCard className="p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Subscriptions ({filtered.length})
            </Label>
            <div className="relative w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <Input placeholder="Search by name or email…" className="pl-8 h-7 text-xs rounded-xl"
                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
          </div>

          {isLoading ? (
            <div className="dash-empty-state flex flex-row items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="animate-spin size-4" /> Loading subscriptions…
            </div>
          ) : filtered.length === 0 ? (
            <div className="dash-empty-state">
              <Users className="size-10 mx-auto mb-4 opacity-30" />
              <p className="text-sm">No subscriptions yet</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
              {filtered.map((sub: MockTestSubscription) => {
                const isActive = sub.status === "ACTIVE" && new Date(sub.expiresAt) > new Date();
                return (
                  <div key={sub.id}
                    className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border border-border/60 bg-muted/20 hover:bg-muted/40 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                        isActive ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"
                      }`}>
                        {String(sub.userId).slice(-2)}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {(sub.user as any)?.name || (sub.user as any)?.email || `User #${sub.userId}`}
                        </div>
                        <div className="text-[10px] text-muted-foreground flex flex-wrap items-center gap-2">
                          <span className="font-mono">ID: {sub.userId}</span>
                          <span>·</span>
                          <span>{sub.planTitle || `${sub.durationMonths} month${sub.durationMonths !== 1 ? "s" : ""}`}</span>
                          <span>·</span>
                          <span className="font-mono">{sub.amountPaid > 0 ? `${sub.currency} ${sub.amountPaid}` : "Free"}</span>
                          <span>·</span>
                          <span className="capitalize font-mono">{sub.paymentMethod ?? "—"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-center">
                        <div className={`text-xs font-mono ${isActive ? "text-emerald-600" : "text-rose-600"}`}>
                          <CalendarClock className="size-3 inline mr-0.5" />
                          {new Date(sub.expiresAt).toLocaleDateString()}
                        </div>
                        <div className="text-[9px] text-muted-foreground">{isActive ? "Active until" : sub.status.toLowerCase()}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </KCard>
      </div>
    </DashboardLayout>
  );
}
